import * as Sentry from '@sentry/react';
import { logSwitchCaptureFailure } from 'features/user-media/utils/logSwitchCaptureFailure';
import { stopStreamTracks } from 'features/user-media/utils';
import { selectActiveVirtualBackground } from 'features/virtual-backgrounds/selectors';
import { SelectedVirtualBackground } from 'features/virtual-backgrounds/types';
import { virtualBackgroundActivationFailed } from 'features/virtual-backgrounds/virtualBackgroundsSlice';
import { JanusJS } from 'lib/janus';
import { store } from 'store/store';
import { logger } from 'utils/logger';
import { NO_PUBLISHING_FEED } from 'utils/webrtc/errors';
import { getVirtualBackgroundFailureReason } from 'utils/webrtc/virtual-background/gateErrors';
import { VirtualBackgroundManager } from 'utils/webrtc/virtual-background/VirtualBackgroundManager';
import { FeedMediaStates, PublishingKind } from 'utils/webrtc/types';
import { ControlledPublishingHandle } from 'utils/webrtc/publishing/ControlledPublishingHandle';
import { VideoBroadcastReconciler } from 'utils/webrtc/publishing/VideoBroadcastReconciler';

// Host members are read at call time, never captured: the legacy suite monkey-patches
// `activateVirtualBackground` on the feed, and a captured reference would bypass the patch.
export interface VirtualBackgroundCoordinatorHost {
  handle?: ControlledPublishingHandle;
  kind: PublishingKind;
  mediaStates: FeedMediaStates;
  mediaController: {
    requestExternalStream: (constraints?: MediaStreamConstraints) => Promise<MediaStream>;
  };
  getVideoMid: () => string | undefined;
  getActiveMediaConfig: () => {
    video?: boolean | MediaTrackConstraints;
    audio?: boolean | MediaTrackConstraints;
    stream?: MediaStream;
  };
  activateVirtualBackground: (
    config: SelectedVirtualBackground,
    params?: { stream?: MediaStream }
  ) => Promise<MediaStream | undefined>;
}

export class VirtualBackgroundCoordinator {
  constructor(
    private host: VirtualBackgroundCoordinatorHost,
    private reconciler: VideoBroadcastReconciler
  ) {}

  changeVirtualBackground = async (config: SelectedVirtualBackground): Promise<boolean> => {
    if (VirtualBackgroundManager.activated) {
      logger
        .remote({ action: true, capture: 'virtual-background' })
        .log('The user changed virtual background', config);

      // Same processed track, no swap — the provider re-renders in place.
      await VirtualBackgroundManager.changeBackground(config);
      return true;
    }

    const streamVB = await this.host.activateVirtualBackground(config);
    if (!streamVB) {
      return false;
    }

    if (this.host.mediaStates.video.broadcasting) {
      logger
        .remote({ capture: 'virtual-background' })
        .log('The user applied virtual background', config);

      if (!this.host.handle) {
        throw NO_PUBLISHING_FEED(this.host.kind);
      }

      // replaceVideoTrack resolves silently with no track or no mid, so reject those here:
      // reporting an unswapped sender as applied syncs the new value out over the old track.
      const track = streamVB.getVideoTracks()[0];
      const mid = this.host.getVideoMid();
      if (!track || !mid) {
        return false;
      }

      await this.reconciler.replaceVideoTrack(track, mid);
    } else {
      await VirtualBackgroundManager.deactivate();
      stopStreamTracks(streamVB);
    }

    return true;
  };

  resetVirtualBackground = async () => {
    logger.remote({ action: true }).log('The user disabled virtual background');

    if (!this.host.mediaStates.video.broadcasting) {
      await VirtualBackgroundManager.deactivate();
      return;
    }

    // Grab the raw camera before deactivating VB so the sender never briefly carries a
    // dead canvas track. On capture failure, leave VB active — a dead sender is worse.
    let track: MediaStreamTrack | undefined;
    try {
      const stream = await this.host.mediaController.requestExternalStream({
        video: this.host.getActiveMediaConfig().video,
      });
      track = stream?.getVideoTracks()[0];
    } catch (error) {
      logSwitchCaptureFailure('Virtual background reset', error);
      return;
    }

    if (!track) {
      return;
    }

    await this.reconciler.replaceVideoTrack(track, this.host.getVideoMid());
    await VirtualBackgroundManager.deactivate();
  };

  activateVirtualBackground = async (
    config: SelectedVirtualBackground,
    params: { stream?: MediaStream } = {}
  ): Promise<MediaStream | undefined> => {
    logger.remote().log('Applying virtual background', config);

    let externalStream: MediaStream | undefined;
    try {
      externalStream = params.stream || (await this.host.mediaController.requestExternalStream());

      const resultedStream = await VirtualBackgroundManager.activateBackground({
        type: config.type,
        value: config.value,
        stream: externalStream,
      });

      if (!resultedStream) {
        if (!params.stream) {
          stopStreamTracks(externalStream);
        }
        return undefined;
      }

      // Safe to stop: Manager cloned the video track and returns no audio
      if (!params.stream) {
        stopStreamTracks(externalStream);
      }

      return resultedStream;
    } catch (error) {
      if (externalStream && !params.stream) {
        stopStreamTracks(externalStream);
      }
      const reason = getVirtualBackgroundFailureReason(error);
      if (reason) {
        store.dispatch(virtualBackgroundActivationFailed({ reason, requested: config }));
      }
      return undefined;
    }
  };

  shouldRestoreVirtualBackground = (offerParams: JanusJS.OfferParams) => {
    const selectedVB = selectActiveVirtualBackground(store.getState());

    return !offerParams.stream && !VirtualBackgroundManager.activated && selectedVB.value;
  };

  restoreVirtualBackground = async () => {
    const selectedVB = selectActiveVirtualBackground(store.getState());

    try {
      return await this.host.activateVirtualBackground(selectedVB);
    } catch (error) {
      Sentry.captureException(error);
      logger.remote().error('Failed to restore virtual background:', error);

      return undefined;
    }
  };
}
